/**
 * Frota (Administração) — veículos, manutenções e o monitoramento dos
 * abastecimentos (05/08, desenho do Lucas).
 *
 * O abastecimento em si entra pelo MOTORISTA (foto do recibo + km); aqui é a
 * gestão: cadastrar veículo, vincular o motorista habitual, lançar manutenção
 * (com foto da NF ou entrada direta sem nota), agendar troca de óleo/filtros
 * (por km ou data — vira aviso para o motorista e para cá) e ler o histórico
 * completo com consumo médio.
 */

import { api, ErroApi } from '../../api.js';
import { dataHora, escapar, numero } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';

interface Veiculo {
  id: number;
  placa: string;
  descricao: string | null;
  marca_modelo: string | null;
  ano: number | null;
  km_atual: number;
  motorista_id: number | null;
  motoristaNome: string | null;
  ultimoConsumo: number | null;
  consumoMedio: number | null;
  manutencoesVencendo: number;
}

interface Pessoa {
  id: number;
  nome: string;
  modulos: string[];
}

function dataBr(v: string | null): string {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
}

const reais = (v: number | null) =>
  v === null ? '—' : `R$ ${v.toFixed(2).replace('.', ',')}`;

export function montarFrota(casco: HTMLElement, abaInicial: 'veiculos' | 'revalidacao' | 'relatorios' = 'veiculos'): void {
  // As ABAS da Frota (18/08, Lucas): Veículos (tudo que já existia),
  // Revalidação (comprovantes divergentes aguardando um humano) e Relatórios
  // (recortes com link público e envio mensal por e-mail). O `raiz` daqui
  // para baixo é o corpo da aba ativa — o código antigo não mudou.
  casco.innerHTML = `
    <nav class="fr-abas">
      <button class="chip chip-ativo" data-fr-aba="veiculos">🚛 Veículos</button>
      <button class="chip" data-fr-aba="revalidacao">🧾 Revisão</button>
      <button class="chip" data-fr-aba="relatorios">📊 Relatórios</button>
    </nav>
    <div data-fr-corpo></div>`;
  const raiz = casco.querySelector<HTMLElement>('[data-fr-corpo]')!;
  casco.querySelectorAll<HTMLElement>('[data-fr-aba]').forEach((b) => {
    b.addEventListener('click', () => {
      casco
        .querySelectorAll('[data-fr-aba]')
        .forEach((x) => x.classList.toggle('chip-ativo', x === b));
      const aba = b.dataset['frAba'];
      if (aba === 'revalidacao') void montarRevalidacao();
      else if (aba === 'relatorios') void montarRelatorios();
      else void listar();
    });
  });

  let motoristas: Pessoa[] = [];

  async function listar(): Promise<void> {
    raiz.innerHTML = `<p class="carregando">Carregando a frota…</p>`;
    try {
      const [rv, rp] = await Promise.all([
        api.get<{ veiculos: Veiculo[] }>('/api/frota/veiculos'),
        api.get<{ pessoas: Pessoa[] }>('/api/pessoas?status=ativo'),
      ]);
      motoristas = rp.pessoas.filter((p) => p.modulos.includes('motorista'));
      desenhar(rv.veiculos);
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar a frota.',
      )}</p>`;
    }
  }

  function desenhar(veiculos: Veiculo[]): void {
    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>🚛 Veículos <span class="sub-inline">${veiculos.length}</span></h3>
        ${
          veiculos.length === 0
            ? `<p class="dica">Nenhum veículo ainda — cadastre abaixo.</p>`
            : `<div class="rolagem"><table class="tabela"><thead><tr>
                 <th>Placa</th><th>Veículo</th><th>Motorista</th><th class="num">Km atual</th>
                 <th class="num">Últ. consumo</th><th class="num">Média</th><th></th>
               </tr></thead><tbody>
               ${veiculos
                 .map(
                   (v) => `<tr>
                   <td class="cod">${escapar(v.placa)}</td>
                   <td>${escapar(v.descricao ?? v.marca_modelo ?? '—')}${
                     v.manutencoesVencendo
                       ? ` <span class="fe-vencido">⚠️ ${v.manutencoesVencendo} manutenção(ões) vencendo</span>`
                       : ''
                   }</td>
                   <td>${escapar(v.motoristaNome ?? '—')}</td>
                   <td class="num">${v.km_atual.toLocaleString('pt-BR')}</td>
                   <td class="num">${v.ultimoConsumo !== null ? v.ultimoConsumo.toFixed(1).replace('.', ',') + ' km/l' : '—'}</td>
                   <td class="num">${v.consumoMedio !== null ? v.consumoMedio.toFixed(1).replace('.', ',') + ' km/l' : '—'}</td>
                   <td><button class="btn btn-claro" data-ver="${escapar(v.placa)}">Abrir</button></td>
                 </tr>`,
                 )
                 .join('')}
               </tbody></table></div>`
        }
      </section>

      <section class="ped-painel">
        <h3>➕ Cadastrar veículo</h3>
        <div class="form-2col">
          <label class="rotulado"><span>Placa</span><input id="fr-placa" maxlength="10" placeholder="PRE3585"></label>
          <label class="rotulado"><span>Descrição</span><input id="fr-desc" maxlength="200" placeholder="Caminhão baú branco"></label>
          <label class="rotulado"><span>Marca/modelo</span><input id="fr-modelo" maxlength="120"></label>
          <label class="rotulado"><span>Ano</span><input id="fr-ano" type="number" min="1950" max="2100" inputmode="numeric"></label>
          <label class="rotulado"><span>Motorista habitual</span>
            <select id="fr-motorista"><option value="">— nenhum —</option>
              ${motoristas.map((m) => `<option value="${m.id}">${escapar(m.nome)}</option>`).join('')}
            </select></label>
          <label class="rotulado"><span>Km atual</span><input id="fr-km" type="number" min="0" inputmode="numeric"></label>
        </div>
        <div class="conf-acoes"><button class="btn" id="fr-criar">✓ Cadastrar veículo</button></div>
      </section>`;

    raiz.querySelectorAll<HTMLElement>('[data-ver]').forEach((b) => {
      b.addEventListener('click', () => void abrirVeiculo(b.dataset['ver']!));
    });
    raiz.querySelector('#fr-criar')!.addEventListener('click', () => {
      const placa = raiz.querySelector<HTMLInputElement>('#fr-placa')!.value.trim();
      if (!placa) return avisarBloqueante('Informe a placa.');
      const motorista = raiz.querySelector<HTMLSelectElement>('#fr-motorista')!.value;
      void api
        .post('/api/frota/veiculos', {
          placa,
          descricao: raiz.querySelector<HTMLInputElement>('#fr-desc')!.value.trim() || undefined,
          marcaModelo: raiz.querySelector<HTMLInputElement>('#fr-modelo')!.value.trim() || undefined,
          ano: Number(raiz.querySelector<HTMLInputElement>('#fr-ano')!.value) || undefined,
          motoristaId: motorista ? Number(motorista) : undefined,
          kmAtual: Number(raiz.querySelector<HTMLInputElement>('#fr-km')!.value) || 0,
        })
        .then(() => {
          avisar(`Veículo ${placa.toUpperCase()} cadastrado.`);
          void listar();
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui cadastrar.'));
    });
  }

  // ------------------------------------------------------- ficha do veículo

  async function abrirVeiculo(placa: string): Promise<void> {
    raiz.innerHTML = `<p class="carregando">Abrindo ${escapar(placa)}…</p>`;
    try {
      const h = await api.get<{
        veiculo: { placa: string; descricao: string | null; marca_modelo: string | null; ano: number | null; km_atual: number; motorista_id: number | null; oleo_ultima_km: number | null; oleo_proxima_km: number | null; pneus_ultima_km: number | null };
        manutencoes: { id: number; tipo: string; descricao: string | null; valor: number | null; nf_numero: string | null; nf_foto: string | null; sem_nota: boolean; km: number | null; ocorrido_em: string; criadoPor: string | null; pecas_valor: number | null; mao_obra_valor: number | null; oficina: string | null; notas?: { url: string | null; tipoMime: string; categoria?: string; numero?: string; fornecedor?: string; valor?: number }[] }[];
        abastecimentos: { id: number; ocorrido_em: string; litros: number; centro_custo?: string | null; sem_requisicao?: boolean; valor_total: number | null; preco_litro: number | null; combustivel: string | null; posto: string | null; km: number; km_lido_ia: number | null; km_corrigido: boolean; km_ajuste: { de: number; para: number; por: string | null; em: string; motivo?: string } | null; recibo_foto: string | null; consumo_km_l: number | null; motoristaNome: string | null }[];
        agendadas: { id: number; tipo: string; a_cada_km: number | null; proximo_km: number | null; proxima_data: string | null; vencida: boolean }[];
        totais: { manutencao: number; combustivel: number };
      }>(`/api/frota/veiculos/${encodeURIComponent(placa)}/historico`);
      const v = h.veiculo;

      raiz.innerHTML = `
        <button class="voltar" id="fr-voltar">← Frota</button>
        <section class="ped-painel">
          <header class="ficha-topo"><div>
            <span class="rotulo">${escapar(v.marca_modelo ?? 'Veículo')}${v.ano ? ` · ${v.ano}` : ''}</span>
            <h2>${escapar(v.placa)} — ${escapar(v.descricao ?? '')}</h2>
            <span class="sub-inline">km atual: <b>${v.km_atual.toLocaleString('pt-BR')}</b>
              <button class="fr-km-editar" id="mv-km-corrigir" type="button" title="Corrigir a quilometragem do veículo">✏️</button>
              · gasto em manutenção: <b>${reais(h.totais.manutencao)}</b>
              · em combustível: <b>${reais(h.totais.combustivel)}</b></span>
          </div></header>

          <h3>🛢️ Óleo e pneus</h3>
          ${(() => {
            const oleoFalta = v.oleo_proxima_km !== null ? v.oleo_proxima_km - v.km_atual : null;
            const pneusRodados = v.pneus_ultima_km !== null ? v.km_atual - v.pneus_ultima_km : null;
            return `
              ${
                oleoFalta !== null && oleoFalta <= 1000
                  ? `<p class="fe-vencido">⚠️ Troca de óleo ${oleoFalta > 0 ? `em ${oleoFalta.toLocaleString('pt-BR')} km` : `VENCIDA (era aos ${v.oleo_proxima_km!.toLocaleString('pt-BR')} km)`} — motorista avisado.</p>`
                  : ''
              }
              ${
                pneusRodados !== null && pneusRodados >= 40000
                  ? `<p class="fe-vencido">⚠️ Checar os pneus — ${pneusRodados.toLocaleString('pt-BR')} km desde a última troca (checagem dos ${(Math.floor(pneusRodados / 10000) * 10000).toLocaleString('pt-BR')}).</p>`
                  : ''
              }
              <div class="form-2col">
                <label class="rotulado"><span>Última troca de óleo (km)</span>
                  <input id="mv-oleo-ultima" type="number" min="0" inputmode="numeric" value="${v.oleo_ultima_km ?? ''}"></label>
                <label class="rotulado"><span>Próxima troca de óleo (km) — avisa faltando 1.000</span>
                  <input id="mv-oleo-proxima" type="number" min="0" inputmode="numeric" value="${v.oleo_proxima_km ?? ''}"></label>
                <label class="rotulado"><span>Última troca de pneus (km) — checagem a cada 10.000 após 40.000 rodados</span>
                  <input id="mv-pneus" type="number" min="0" inputmode="numeric" value="${v.pneus_ultima_km ?? ''}"></label>
              </div>
              <button class="btn btn-claro" id="mv-salvar">✓ Salvar óleo/pneus</button>`;
          })()}

          <h3>🗓️ Manutenções agendadas</h3>
          ${
            h.agendadas.length === 0
              ? `<p class="dica">Nenhuma agendada.</p>`
              : h.agendadas
                  .map(
                    (a) => `<div class="fr-agendada ${a.vencida ? 'vencida' : ''}">
                    ${a.vencida ? '⚠️ ' : ''}<b>${escapar(a.tipo)}</b>
                    ${a.proximo_km ? ` · aos ${a.proximo_km.toLocaleString('pt-BR')} km` : ''}
                    ${a.a_cada_km ? ` (a cada ${a.a_cada_km.toLocaleString('pt-BR')} km)` : ''}
                    ${a.proxima_data ? ` · em ${dataBr(a.proxima_data)}` : ''}
                    <button class="ped-tirar" data-tirar-ag="${a.id}" title="Remover">✕</button>
                  </div>`,
                  )
                  .join('')
          }
          <div class="form-2col fr-nova-ag">
            <label class="rotulado"><span>Tipo (troca de óleo, filtro…)</span><input id="ag-tipo" maxlength="120"></label>
            <label class="rotulado"><span>A cada (km)</span><input id="ag-cada" type="number" min="1" inputmode="numeric"></label>
            <label class="rotulado"><span>ou no km</span><input id="ag-km" type="number" min="1" inputmode="numeric"></label>
            <label class="rotulado"><span>ou na data</span><input id="ag-data" type="date"></label>
          </div>
          <button class="btn btn-claro" id="ag-criar">+ Agendar</button>

          <h3>🔧 Lançar manutenção</h3>
          <div class="form-2col">
            <label class="rotulado"><span>Tipo</span><input id="mn-tipo" maxlength="120" placeholder="troca de óleo · freios · suspensão"></label>
            <label class="rotulado"><span>Oficina</span><input id="mn-oficina" maxlength="200"></label>
            <label class="rotulado"><span>Peças (R$)</span><input id="mn-pecas" type="number" min="0" step="0.01" inputmode="decimal"></label>
            <label class="rotulado"><span>Mão de obra (R$)</span><input id="mn-mao" type="number" min="0" step="0.01" inputmode="decimal"></label>
            <label class="rotulado"><span>Total (R$) — vazio soma os dois</span><input id="mn-valor" type="number" min="0" step="0.01" inputmode="decimal"></label>
            <label class="rotulado"><span>Nº da NF</span><input id="mn-nf" maxlength="60"></label>
            <label class="rotulado largo"><span>📎 Notas fiscais — foto ou PDF, pode mais de uma (serviço, peças…)</span>
              <span class="ab-foto-botoes">
                <button class="btn btn-claro" type="button" id="mn-nota-camera">📷 Tirar foto</button>
                <button class="btn btn-claro" type="button" id="mn-nota-arquivos">📁 Enviar arquivos</button>
                <span class="dica" id="mn-nota-lendo" hidden>🤖 Lendo a nota…</span>
              </span>
              <input id="mn-foto" type="file" accept="image/*" capture="environment" hidden>
              <input id="mn-arquivos" type="file" accept="image/*,application/pdf" multiple hidden>
              <ul class="mn-notas" id="mn-notas"></ul>
              <small class="dica">A IA lê cada nota e preenche oficina, peças, mão de obra, total, nº, data e tipo — confira antes de lançar.</small>
            </label>
            <label class="rotulado"><span>Km</span><input id="mn-km" type="number" min="0" inputmode="numeric" value="${v.km_atual}"></label>
            <label class="rotulado"><span>Data</span><input id="mn-data" type="date"></label>
          </div>
          <label class="rotulado"><span>Descrição (opcional)</span><input id="mn-desc" maxlength="500"></label>
          <label class="conf-fotos-usar"><input type="checkbox" id="mn-semnota"><span>Entrada direta, sem nota</span></label>
          <div class="conf-acoes"><button class="btn" id="mn-criar">✓ Lançar manutenção</button></div>

          <h3>Histórico de manutenções <span class="sub-inline">${h.manutencoes.length}</span></h3>
          ${
            h.manutencoes.length === 0
              ? `<p class="dica">Nenhuma manutenção lançada.</p>`
              : `<div class="rolagem"><table class="tabela"><thead><tr>
                   <th>Data</th><th>Tipo</th><th>Oficina</th><th>Descrição</th><th class="num">Km</th>
                   <th class="num">Peças</th><th class="num">M. obra</th><th class="num">Total</th><th>NF</th>
                 </tr></thead><tbody>${h.manutencoes
                   .map(
                     (m) => `<tr>
                     <td>${dataBr(m.ocorrido_em)}</td>
                     <td>${escapar(m.tipo)}</td>
                     <td>${escapar(m.oficina ?? '—')}</td>
                     <td>${escapar(m.descricao ?? '—')}</td>
                     <td class="num">${m.km ? m.km.toLocaleString('pt-BR') : '—'}</td>
                     <td class="num">${reais(m.pecas_valor)}</td>
                     <td class="num">${reais(m.mao_obra_valor)}</td>
                     <td class="num">${reais(m.valor)}</td>
                     <td>${
                       m.sem_nota
                         ? 'sem nota'
                         : `${escapar(m.nf_numero ?? '—')}${
                             m.notas?.length
                               ? m.notas
                                   .map((n) =>
                                     n.tipoMime === 'application/pdf'
                                       ? ` <a class="fr-nota-pdf" href="${escapar(api.foto(n.url) ?? '')}" target="_blank" rel="noopener" title="${escapar(n.fornecedor ?? '')}">📄 ${escapar(n.categoria ?? 'nota')}</a>`
                                       : ` <img class="fr-mini" src="${escapar(api.foto(n.url) ?? '')}" data-grande="${escapar(api.foto(n.url) ?? '')}" alt="" title="${escapar(n.categoria ?? '')}">`,
                                   )
                                   .join('')
                               : m.nf_foto
                                 ? ` <img class="fr-mini" src="${escapar(api.foto(m.nf_foto) ?? '')}" data-grande="${escapar(api.foto(m.nf_foto) ?? '')}" alt="">`
                                 : ''
                           }`
                     }</td>
                   </tr>`,
                   )
                   .join('')}</tbody></table></div>`
          }

          <h3>⛽ Abastecimentos <span class="sub-inline">${h.abastecimentos.length} — registrados pelo motorista</span></h3>
          ${
            h.abastecimentos.length === 0
              ? `<p class="dica">Nenhum abastecimento ainda — o motorista registra pelo módulo dele, com a foto do recibo.</p>`
              : `<div class="rolagem"><table class="tabela"><thead><tr>
                   <th>Quando</th><th>Quem</th><th class="num">Km</th><th class="num" title="Km rodados desde o abastecimento anterior — acima de 1.000 é improvável">Rodou</th><th class="num">Litros</th>
                   <th class="num">R$/l</th><th class="num">Total</th><th class="num">Consumo</th><th>Posto</th><th></th>
                 </tr></thead><tbody>${h.abastecimentos
                   .map((a, i, todos) => {
                     // A lista vem do maior km para o menor: o "anterior" é a linha de baixo.
                     const antes = todos[i + 1];
                     const rodou = antes ? a.km - antes.km : null;
                     const salto = rodou !== null && rodou > 1000;
                     const ajuste = a.km_ajuste;
                     return `<tr${salto ? ' class="fr-salto"' : ''}>
                     <td>${dataHora(a.ocorrido_em)}</td>
                     <td>${escapar(a.motoristaNome ?? '—')}</td>
                     <td class="num">${a.km.toLocaleString('pt-BR')}${
                       ajuste
                         ? ` <span class="fr-km-corrigido" title="Corrigida pela Administração: era ${ajuste.de.toLocaleString('pt-BR')}${
                             ajuste.por ? `, por ${escapar(ajuste.por)}` : ''
                           } em ${dataHora(ajuste.em)}${ajuste.motivo ? ` — ${escapar(ajuste.motivo)}` : ''}">🛠️</span>`
                         : a.km_corrigido
                           ? ` <span class="fr-km-corrigido" title="Km corrigida à mão pelo motorista${
                               a.km_lido_ia !== null ? ` (a foto lia ${a.km_lido_ia.toLocaleString('pt-BR')})` : ''
                             }">✏️</span>`
                           : ''
                     } <button class="fr-km-editar" type="button" data-km-ab="${a.id}" data-km-atual="${a.km}" title="Corrigir a km deste abastecimento">✏️</button></td>
                     <td class="num">${rodou === null ? '—' : `${salto ? '⚠️ ' : ''}${rodou.toLocaleString('pt-BR')}`}</td>
                     <td class="num">${numero(a.litros)}</td>
                     <td class="num">${a.preco_litro !== null ? a.preco_litro.toFixed(2).replace('.', ',') : '—'}</td>
                     <td class="num">${reais(a.valor_total)}</td>
                     <td class="num">${a.consumo_km_l !== null ? `<b>${a.consumo_km_l.toFixed(1).replace('.', ',')} km/l</b>` : '—'}</td>
                     <td>${escapar(a.posto ?? '—')}${a.sem_requisicao ? ' <span class="sub-inline">· sem requisição</span>' : ''}${
                       a.centro_custo ? `<br><span class="sub-inline">CC: ${escapar(a.centro_custo)}</span>` : ''
                     }</td>
                     <td>${a.recibo_foto ? `<img class="fr-mini" src="${escapar(api.foto(a.recibo_foto) ?? '')}" data-grande="${escapar(api.foto(a.recibo_foto) ?? '')}" alt="">` : ''}</td>
                   </tr>`;
                   })
                   .join('')}</tbody></table></div>`
          }
        </section>`;

      raiz.querySelector('#fr-voltar')!.addEventListener('click', () => void listar());

      // Correção de quilometragem (10/09, Lucas): o km do veículo e o km de
      // um abastecimento já lançado. Um recibo lido sem a vírgula do hodômetro
      // (2765813 em vez de 276581) inflava o km e trancava os seguintes.
      const lerKm = (resposta: string | null): number | null => {
        if (resposta === null) return null;
        const km = Number(resposta.replace(/\D/g, ''));
        if (!Number.isInteger(km) || km < 0 || resposta.trim() === '') {
          avisarBloqueante('Digite só números.');
          return null;
        }
        return km;
      };
      raiz.querySelector('#mv-km-corrigir')!.addEventListener('click', () => {
        const km = lerKm(
          window.prompt(
            `Quilometragem atual do ${placa} (hoje: ${v.km_atual.toLocaleString('pt-BR')} km).\n\nSe o erro veio de um abastecimento, corrija a km DELE na tabela de abastecimentos (✏️ ao lado da km) — o km do veículo acompanha sozinho.`,
            String(v.km_atual),
          ),
        );
        if (km === null || km === v.km_atual) return;
        void api
          .put(`/api/frota/veiculos/${encodeURIComponent(placa)}`, { kmAtual: km })
          .then(() => {
            avisar(`Km do veículo corrigida para ${km.toLocaleString('pt-BR')}.`);
            void abrirVeiculo(placa);
          })
          .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui corrigir.'));
      });
      raiz.querySelectorAll<HTMLElement>('[data-km-ab]').forEach((b) => {
        b.addEventListener('click', () => {
          const atual = Number(b.dataset['kmAtual']);
          const km = lerKm(
            window.prompt(
              `Km certa deste abastecimento (lançada: ${atual.toLocaleString('pt-BR')}).\n\nO consumo deste trecho e do seguinte é recalculado e a correção fica registrada.`,
              String(atual),
            ),
          );
          if (km === null || km === atual) return;
          const motivo = (window.prompt('Motivo (opcional) — ex.: "lido sem a vírgula do hodômetro".') ?? '').trim();
          const corrigir = (corrigirAnteriores: boolean): Promise<void> =>
            api
              .put<{ corrigidosAntes?: { id: number; de: number; para: number }[] }>(
                `/api/frota/abastecimentos/${b.dataset['kmAb']}/km`,
                { km, ...(motivo ? { motivo } : {}), ...(corrigirAnteriores ? { corrigirAnteriores: true } : {}) },
              )
              .then((r) => {
                const antes = r.corrigidosAntes?.length
                  ? ` Também corrigi ${r.corrigidosAntes.length} anterior(es) sem vírgula: ${r.corrigidosAntes
                      .map((c) => `${c.de.toLocaleString('pt-BR')} → ${c.para.toLocaleString('pt-BR')}`)
                      .join(', ')}.`
                  : '';
                avisar(`Km corrigida para ${km.toLocaleString('pt-BR')}.${antes}`);
                void abrirVeiculo(placa);
              })
              .catch((e) => {
                // O anterior também está sem a vírgula (14/09): oferece a
                // correção em cadeia em vez de mandar a pessoa achar a ordem.
                if (!corrigirAnteriores && e instanceof ErroApi && e.sugestao && e.message.includes('sem a vírgula')) {
                  if (window.confirm(`${e.message}\n\n${e.sugestao}\n\nCorrigir os anteriores sem vírgula e depois este?`)) {
                    return corrigir(true);
                  }
                  return;
                }
                avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui corrigir.');
              });
          void corrigir(false);
        });
      });
      raiz.querySelectorAll<HTMLElement>('[data-tirar-ag]').forEach((b) => {
        b.addEventListener('click', () => {
          void api
            .del(`/api/frota/agendadas/${b.dataset['tirarAg']}`)
            .then(() => abrirVeiculo(placa))
            .catch(() => avisarBloqueante('Não consegui remover.'));
        });
      });
      raiz.querySelector('#mv-salvar')!.addEventListener('click', () => {
        const n = (id: string) => {
          const v2 = raiz.querySelector<HTMLInputElement>(id)!.value.trim();
          return v2 === '' ? null : Number(v2);
        };
        void api
          .put(`/api/frota/veiculos/${encodeURIComponent(placa)}`, {
            oleoUltimaKm: n('#mv-oleo-ultima'),
            oleoProximaKm: n('#mv-oleo-proxima'),
            pneusUltimaKm: n('#mv-pneus'),
          })
          .then(() => {
            avisar('Óleo/pneus salvos — os avisos foram re-armados.');
            void abrirVeiculo(placa);
          })
          .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui salvar.'));
      });

      raiz.querySelector('#ag-criar')!.addEventListener('click', () => {
        const tipo = raiz.querySelector<HTMLInputElement>('#ag-tipo')!.value.trim();
        if (!tipo) return avisarBloqueante('Diga o tipo (troca de óleo, filtro…).');
        const cada = Number(raiz.querySelector<HTMLInputElement>('#ag-cada')!.value) || undefined;
        const km = Number(raiz.querySelector<HTMLInputElement>('#ag-km')!.value) || undefined;
        const data = raiz.querySelector<HTMLInputElement>('#ag-data')!.value || undefined;
        void api
          .post(`/api/frota/veiculos/${encodeURIComponent(placa)}/agendadas`, {
            tipo,
            ...(cada ? { aCadaKm: cada } : {}),
            ...(km ? { proximoKm: km } : {}),
            ...(data ? { proximaData: data } : {}),
          })
          .then(() => {
            avisar('Manutenção agendada — vira aviso para o motorista.');
            void abrirVeiculo(placa);
          })
          .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui agendar.'));
      });
      /**
       * 📎 NOTAS DA MANUTENÇÃO (10/09, Lucas): cada arquivo (foto ou PDF) sobe,
       * a IA lê, e a ficha se preenche SOMANDO as notas — serviço + peças.
       * A pessoa pode corrigir qualquer campo antes de lançar.
       */
      interface NotaLida {
        caminho: string;
        tipoMime: string;
        lido: {
          fornecedor?: string; cnpj?: string; numero?: string; emitidaEm?: string; valorTotal?: number;
          valorServicos?: number; valorPecas?: number; categoria?: 'servico' | 'pecas' | 'mista' | 'outra';
          tipoSugerido?: string; resumo?: string; placa?: string; km?: number; erro?: string;
        };
      }
      const notas: NotaLida[] = [];
      const listaNotas = raiz.querySelector<HTMLElement>('#mn-notas')!;
      const campo = (id: string) => raiz.querySelector<HTMLInputElement>(id)!;
      const dinheiro = (n: number) => n.toFixed(2).replace('.', ',');

      const desenharNotas = (): void => {
        listaNotas.innerHTML = notas
          .map((n, i) => {
            const l = n.lido;
            const rot = l.erro
              ? `⚠️ ${escapar(l.erro)}`
              : `${escapar(l.categoria === 'servico' ? 'serviço' : l.categoria === 'pecas' ? 'peças' : l.categoria === 'mista' ? 'serviço + peças' : 'nota')}${
                  l.fornecedor ? ` · ${escapar(l.fornecedor)}` : ''
                }${l.numero ? ` · nº ${escapar(l.numero)}` : ''}${
                  typeof l.valorTotal === 'number' ? ` · R$ ${dinheiro(l.valorTotal)}` : ''
                }`;
            return `<li>${n.tipoMime === 'application/pdf' ? '📄' : '🖼️'} ${rot}
              <button type="button" class="btn btn-claro mn-nota-tirar" data-tirar="${i}">✕</button></li>`;
          })
          .join('');
        listaNotas.querySelectorAll<HTMLElement>('[data-tirar]').forEach((b) =>
          b.addEventListener('click', () => {
            notas.splice(Number(b.dataset['tirar']), 1);
            desenharNotas();
            preencherDasNotas();
          }),
        );
      };

      const preencherDasNotas = (): void => {
        const lidas = notas.map((n) => n.lido).filter((l) => !l.erro);
        if (lidas.length === 0) return;
        const soma = (f: (l: NotaLida['lido']) => number | undefined) =>
          lidas.reduce((acc, l) => acc + (f(l) ?? 0), 0);
        // Serviço/peças: o valor separado quando a nota separa; senão, o total
        // da nota inteira vai para o lado que ela é.
        const pecas = soma((l) => l.valorPecas ?? (l.categoria === 'pecas' ? l.valorTotal : undefined));
        const mao = soma((l) => l.valorServicos ?? (l.categoria === 'servico' ? l.valorTotal : undefined));
        const total = soma((l) => l.valorTotal);
        if (pecas > 0) campo('#mn-pecas').value = pecas.toFixed(2);
        if (mao > 0) campo('#mn-mao').value = mao.toFixed(2);
        if (total > 0) campo('#mn-valor').value = total.toFixed(2);
        const oficina = lidas.find((l) => l.categoria !== 'pecas' && l.fornecedor)?.fornecedor ?? lidas.find((l) => l.fornecedor)?.fornecedor;
        if (oficina && !campo('#mn-oficina').value) campo('#mn-oficina').value = oficina;
        const numeros = lidas.map((l) => l.numero).filter(Boolean);
        if (numeros.length) campo('#mn-nf').value = numeros.join(', ');
        const datas = lidas.map((l) => l.emitidaEm).filter((d): d is string => !!d).sort();
        if (datas[0] && !campo('#mn-data').value) campo('#mn-data').value = datas[0];
        const tipo = lidas.find((l) => l.tipoSugerido)?.tipoSugerido;
        if (tipo && !campo('#mn-tipo').value) campo('#mn-tipo').value = tipo;
        const resumos = lidas.map((l) => l.resumo).filter(Boolean);
        if (resumos.length && !campo('#mn-desc').value) campo('#mn-desc').value = resumos.join('; ').slice(0, 500);
        const kmNota = lidas.find((l) => typeof l.km === 'number' && l.km > 0)?.km;
        if (kmNota && Number(campo('#mn-km').value) === v.km_atual) campo('#mn-km').value = String(kmNota);
        const placaNota = lidas.find((l) => l.placa)?.placa;
        if (placaNota && placaNota.toUpperCase().replace(/[^A-Z0-9]/g, '') !== placa.toUpperCase().replace(/[^A-Z0-9]/g, '')) {
          avisar(`⚠️ A nota está na placa ${placaNota}, e você está lançando no ${placa}.`, 'atencao');
        }
      };

      const subirNota = async (arq: File): Promise<void> => {
        const lendo = raiz.querySelector<HTMLElement>('#mn-nota-lendo')!;
        lendo.hidden = false;
        try {
          const corpo = new FormData();
          corpo.append('arquivo', arq);
          const r = await api.enviarMultipart<NotaLida>('/api/frota/nota-manutencao', corpo);
          notas.push(r);
          desenharNotas();
          preencherDasNotas();
          if (r.lido.erro) avisar(r.lido.erro, 'atencao');
        } catch (e) {
          avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui ler essa nota.');
        } finally {
          lendo.hidden = notas.length === 0 ? true : false;
          lendo.hidden = true;
        }
      };
      raiz.querySelector('#mn-nota-camera')!.addEventListener('click', () => campo('#mn-foto').click());
      raiz.querySelector('#mn-nota-arquivos')!.addEventListener('click', () => campo('#mn-arquivos').click());
      const aoEscolherNotas = (e: Event) => {
        const arquivos = [...((e.target as HTMLInputElement).files ?? [])];
        (e.target as HTMLInputElement).value = '';
        void (async () => {
          for (const arq of arquivos) await subirNota(arq);
        })();
      };
      campo('#mn-foto').addEventListener('change', aoEscolherNotas);
      campo('#mn-arquivos').addEventListener('change', aoEscolherNotas);

      raiz.querySelector('#mn-criar')!.addEventListener('click', () => {
        void (async () => {
          const tipo = raiz.querySelector<HTMLInputElement>('#mn-tipo')!.value.trim();
          if (!tipo) return avisarBloqueante('Diga o tipo da manutenção.');
          const semNota = raiz.querySelector<HTMLInputElement>('#mn-semnota')!.checked;
          try {
            await api.post(`/api/frota/veiculos/${encodeURIComponent(placa)}/manutencoes`, {
              notas: notas
                .filter((n) => !n.lido.erro)
                .map((n) => ({
                  caminho: n.caminho,
                  tipoMime: n.tipoMime,
                  categoria: n.lido.categoria ?? 'outra',
                  ...(n.lido.numero ? { numero: n.lido.numero } : {}),
                  ...(n.lido.fornecedor ? { fornecedor: n.lido.fornecedor } : {}),
                  ...(n.lido.emitidaEm ? { emitidaEm: n.lido.emitidaEm } : {}),
                  ...(typeof n.lido.valorTotal === 'number' ? { valor: n.lido.valorTotal } : {}),
                  lido: n.lido,
                })),
              tipo,
              semNota,
              descricao: raiz.querySelector<HTMLInputElement>('#mn-desc')!.value.trim() || undefined,
              valor: Number(raiz.querySelector<HTMLInputElement>('#mn-valor')!.value) || undefined,
              pecasValor: Number(raiz.querySelector<HTMLInputElement>('#mn-pecas')!.value) || undefined,
              maoObraValor: Number(raiz.querySelector<HTMLInputElement>('#mn-mao')!.value) || undefined,
              oficina: raiz.querySelector<HTMLInputElement>('#mn-oficina')!.value.trim() || undefined,
              nfNumero: raiz.querySelector<HTMLInputElement>('#mn-nf')!.value.trim() || undefined,
              km: Number(raiz.querySelector<HTMLInputElement>('#mn-km')!.value) || undefined,
              ocorridoEm: raiz.querySelector<HTMLInputElement>('#mn-data')!.value || undefined,
            });
            avisar('Manutenção lançada.');
            void abrirVeiculo(placa);
          } catch (e) {
            avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui lançar.');
          }
        })();
      });
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui abrir o veículo.',
      )}</p>`;
    }
  }

  // ------------------------------------------------- aba Revalidação

  interface Pendencia {
    id: number;
    ocorrido_em: string;
    litros: number;
    valor_total: number | null;
    combustivel: string | null;
    posto: string | null;
    km: number;
    recibo_foto: string | null;
    nf_foto: string | null;
    ticket_numero: string | null;
    nf_chave: string | null;
    nf_emitida_em: string | null;
    divergencias: string[] | null;
    placa: string;
    motoristaNome: string | null;
    validado_por: string | null;
    validado_em: string | null;
    preco_litro: number | null;
    nf_numero: string | null;
    centro_custo?: string | null;
    sem_requisicao?: boolean;
    revisao_alteracoes: Record<string, { de: unknown; para: unknown }> | null;
    /** Só nos já revisados. */
    situacao?: 'validado' | 'recusado';
    recusado_por?: string | null;
    recusado_em?: string | null;
    motivo?: string | null;
  }

  const NOMES_CAMPOS: Record<string, string> = {
    km: 'km', litros: 'litros', valorTotal: 'valor total', precoLitro: 'preço/litro',
    combustivel: 'combustível', posto: 'posto', ticketNumero: 'requisição', nfNumero: 'nº da nota', centroCusto: 'centro de custo',
  };
  const mostrarValor = (v: unknown): string =>
    v === null || v === undefined || v === '' ? '—' : typeof v === 'number' ? v.toLocaleString('pt-BR') : String(v);

  async function montarRevalidacao(): Promise<void> {
    raiz.innerHTML = `<p class="carregando">Carregando as pendências…</p>`;
    let dados: { pendentes: Pendencia[]; resolvidos: Pendencia[] };
    try {
      dados = await api.get('/api/frota/revalidacao');
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar.',
      )}</p>`;
      return;
    }

    const cartao = (a: Pendencia, pendente: boolean): string => `
      <article class="fr-revalida">
        <header>
          <b>🚛 ${escapar(a.placa)}</b> · ${dataHora(a.ocorrido_em)}
          ${a.motoristaNome ? ` · ${escapar(a.motoristaNome)}` : ''}
          ${
            pendente
              ? `<span class="fe-vencido">aguardando revisão</span>`
              : a.situacao === 'recusado'
                ? `<span class="fe-vencido">✕ recusado por ${escapar(a.recusado_por ?? '?')} em ${a.recusado_em ? dataHora(a.recusado_em) : '—'}</span>`
                : `<span class="sub-inline">✓ aprovado por ${escapar(a.validado_por ?? '?')} em ${a.validado_em ? dataHora(a.validado_em) : '—'}</span>`
          }
        </header>
        ${a.situacao === 'recusado' && a.motivo ? `<p class="fr-revalida-motivo">Motivo: ${escapar(a.motivo)}</p>` : ''}
        ${
          a.situacao === 'validado' && a.revisao_alteracoes && Object.keys(a.revisao_alteracoes).length
            ? `<p class="sub-inline">✏️ Alterado na revisão: ${Object.entries(a.revisao_alteracoes)
                .map(([c, m]) => `${NOMES_CAMPOS[c] ?? c} ${escapar(mostrarValor(m.de))} → <b>${escapar(mostrarValor(m.para))}</b>`)
                .join(' · ')}</p>`
            : ''
        }
        <p class="sub-inline">${numero(a.litros)} L · ${reais(a.valor_total)} · ${escapar(a.combustivel ?? '—')}
          · km ${a.km.toLocaleString('pt-BR')} · requisição ${a.sem_requisicao ? 'dispensada (posto não cadastrado)' : escapar(a.ticket_numero ?? '—')}${
            a.centro_custo ? ` · centro de custo <b>${escapar(a.centro_custo)}</b>` : ''
          }
          ${a.posto ? ` · ${escapar(a.posto)}` : ''}</p>
        ${
          Array.isArray(a.divergencias) && a.divergencias.length
            ? `<ul class="fr-revalida-lista">${a.divergencias.map((d) => `<li>${escapar(d)}</li>`).join('')}</ul>`
            : ''
        }
        <div class="fr-revalida-fotos">
          ${
            a.recibo_foto
              ? `<figure><img src="${escapar(api.foto(a.recibo_foto) ?? '')}" data-grande="${escapar(api.foto(a.recibo_foto) ?? '')}" alt="Requisição"><figcaption>requisição</figcaption></figure>`
              : '<p class="dica">sem foto da requisição</p>'
          }
          ${
            a.nf_foto
              ? `<figure><img src="${escapar(api.foto(a.nf_foto) ?? '')}" data-grande="${escapar(api.foto(a.nf_foto) ?? '')}" alt="Nota fiscal"><figcaption>nota fiscal${a.nf_emitida_em ? ` · emitida ${dataHora(a.nf_emitida_em)}` : ''}</figcaption></figure>`
              : '<p class="dica">sem foto da nota</p>'
          }
        </div>
        ${
          pendente
            ? `<div class="conf-acoes">
                <button class="btn" type="button" data-aprovar="${a.id}">✓ Aprovar como está</button>
                <button class="btn btn-claro" type="button" data-alterar="${a.id}">✏️ Alterar e aprovar</button>
                <button class="btn btn-claro fr-recusar" type="button" data-recusar="${a.id}">✕ Recusar</button>
              </div>
              <form class="fr-revalida-editar" data-editar="${a.id}" hidden>
                <p class="dica">Corrija o que estiver errado e aprove — o consumo é refeito e a alteração fica anotada no registro.</p>
                <div class="form-2col">
                  <label class="rotulado"><span>Km</span><input name="km" type="number" min="1" inputmode="numeric" value="${a.km}"></label>
                  <label class="rotulado"><span>Litros</span><input name="litros" type="number" step="0.001" min="0.001" value="${a.litros}"></label>
                  <label class="rotulado"><span>Valor total (R$)</span><input name="valorTotal" type="number" step="0.01" min="0" value="${a.valor_total ?? ''}"></label>
                  <label class="rotulado"><span>Preço por litro (R$)</span><input name="precoLitro" type="number" step="0.001" min="0" value="${a.preco_litro ?? ''}"></label>
                  <label class="rotulado"><span>Combustível</span><input name="combustivel" maxlength="60" value="${escapar(a.combustivel ?? '')}"></label>
                  <label class="rotulado"><span>Posto</span><input name="posto" maxlength="200" value="${escapar(a.posto ?? '')}"></label>
                  <label class="rotulado"><span>Nº da requisição</span><input name="ticketNumero" maxlength="40" value="${escapar(a.ticket_numero ?? '')}"></label>
                  <label class="rotulado"><span>Nº da nota</span><input name="nfNumero" maxlength="60" value="${escapar(a.nf_numero ?? '')}"></label>
                  <label class="rotulado"><span>Centro de custo (obra ou setor)</span><input name="centroCusto" maxlength="120" value="${escapar(a.centro_custo ?? '')}"></label>
                </div>
                <div class="conf-acoes">
                  <button class="btn" type="submit">✓ Salvar e aprovar</button>
                  <button class="btn btn-claro" type="button" data-cancelar-editar="${a.id}">Cancelar</button>
                </div>
              </form>`
            : ''
        }
      </article>`;

    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>🧾 Abastecimentos enviados com inconsistência <span class="sub-inline">${dados.pendentes.length}</span></h3>
        <p class="dica">Chegam aqui os abastecimentos que o motorista mandou "mesmo assim": requisição × nota,
          placa, motorista ou km não batiam. A lista diz o quê; olhe as duas fotos e decida: aprovar como
          está, alterar os dados e aprovar, ou recusar (sai da frota, e a requisição fica livre).</p>
        ${dados.pendentes.length === 0 ? '<p class="dica">Nenhuma pendência. 🎉</p>' : dados.pendentes.map((a) => cartao(a, true)).join('')}
      </section>
      ${
        dados.resolvidos.length
          ? `<section class="ped-painel"><h3>Já revisados</h3>${dados.resolvidos.map((a) => cartao(a, false)).join('')}</section>`
          : ''
      }`;

    const falhou = (e: unknown, padrao: string) => avisarBloqueante(e instanceof ErroApi ? e.message : padrao);

    // 1) Aprovar como está.
    raiz.querySelectorAll<HTMLElement>('[data-aprovar]').forEach((b) => {
      b.addEventListener('click', () => {
        void api
          .post(`/api/frota/revalidacao/${b.dataset['aprovar']}/validar`, {})
          .then(() => {
            avisar('Abastecimento aprovado como estava.');
            return montarRevalidacao();
          })
          .catch((e) => falhou(e, 'Não consegui aprovar.'));
      });
    });

    // 2) Alterar e aprovar — abre o formulário; só o que mudou é enviado.
    raiz.querySelectorAll<HTMLElement>('[data-alterar]').forEach((b) => {
      b.addEventListener('click', () => {
        const f = raiz.querySelector<HTMLFormElement>(`form[data-editar="${b.dataset['alterar']}"]`)!;
        f.hidden = !f.hidden;
        if (!f.hidden) f.querySelector<HTMLInputElement>('input')!.focus();
      });
    });
    raiz.querySelectorAll<HTMLElement>('[data-cancelar-editar]').forEach((b) => {
      b.addEventListener('click', () => {
        raiz.querySelector<HTMLFormElement>(`form[data-editar="${b.dataset['cancelarEditar']}"]`)!.hidden = true;
      });
    });
    raiz.querySelectorAll<HTMLFormElement>('form[data-editar]').forEach((f) => {
      f.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const id = Number(f.dataset['editar']);
        const original = dados.pendentes.find((x) => x.id === id);
        if (!original) return;
        const campo = (nome: string) => f.querySelector<HTMLInputElement>(`[name="${nome}"]`)!.value.trim();
        const numero = (nome: string): number | null => (campo(nome) === '' ? null : Number(campo(nome)));
        const corpo: Record<string, unknown> = {};
        const km = numero('km');
        if (km !== null && km !== original.km) corpo['km'] = km;
        const litros = numero('litros');
        if (litros !== null && litros !== original.litros) corpo['litros'] = litros;
        const valorTotal = numero('valorTotal');
        if (valorTotal !== original.valor_total) corpo['valorTotal'] = valorTotal;
        const precoLitro = numero('precoLitro');
        if (precoLitro !== original.preco_litro) corpo['precoLitro'] = precoLitro;
        const textos: [string, string | null][] = [
          ['combustivel', original.combustivel],
          ['posto', original.posto],
          ['ticketNumero', original.ticket_numero],
          ['nfNumero', original.nf_numero],
          ['centroCusto', original.centro_custo ?? null],
        ];
        for (const [nome, de] of textos) {
          const para = campo(nome) === '' ? null : campo(nome);
          if (para !== de) corpo[nome] = para;
        }
        if (!(km !== null && km > 0)) return avisarBloqueante('A km precisa ser um número maior que zero.');
        if (!(litros !== null && litros > 0)) return avisarBloqueante('Os litros precisam ser maiores que zero.');
        if (Object.keys(corpo).length === 0) return avisarBloqueante('Nada foi alterado — use "Aprovar como está".');
        void api
          .post<{ alteracoes: string[] }>(`/api/frota/revalidacao/${id}/validar`, corpo)
          .then((r) => {
            avisar(`Corrigido (${r.alteracoes.map((c) => NOMES_CAMPOS[c] ?? c).join(', ')}) e aprovado.`);
            return montarRevalidacao();
          })
          .catch((e) => falhou(e, 'Não consegui salvar.'));
      });
    });

    // 3) Recusar — com motivo; o registro sai da frota e a requisição fica livre.
    raiz.querySelectorAll<HTMLElement>('[data-recusar]').forEach((b) => {
      b.addEventListener('click', () => {
        const motivo = (window.prompt('Motivo da recusa (o motorista e o histórico vão ver):') ?? '').trim();
        if (!motivo) return;
        if (motivo.length < 3) return avisarBloqueante('Escreva o motivo.');
        void api
          .post<{ aviso: string | null }>(`/api/frota/revalidacao/${b.dataset['recusar']}/recusar`, { motivo })
          .then((r) => {
            if (r.aviso) avisarBloqueante(`Abastecimento recusado. ${r.aviso}`);
            else avisar('Abastecimento recusado — saiu da frota; a requisição e a nota ficam livres para lançar de novo.');
            return montarRevalidacao();
          })
          .catch((e) => falhou(e, 'Não consegui recusar.'));
      });
    });
  }

  // ------------------------------------------------- aba Relatórios

  interface Relatorio {
    id: number;
    nome: string;
    veiculo_ids: number[];
    periodo_de: string | null;
    periodo_ate: string | null;
    dia_envio: number | null;
    periodicidade: 'mensal' | 'quinzenal' | 'semanal';
    emails: string[];
    url: string;
    criado_por: string | null;
    enviado_mes: string | null;
  }

  async function montarRelatorios(): Promise<void> {
    raiz.innerHTML = `<p class="carregando">Carregando os relatórios…</p>`;
    let relatorios: Relatorio[];
    let veiculos: Veiculo[];
    try {
      const [rr, rv] = await Promise.all([
        api.get<{ relatorios: Relatorio[] }>('/api/frota/relatorios'),
        api.get<{ veiculos: Veiculo[] }>('/api/frota/veiculos'),
      ]);
      relatorios = rr.relatorios;
      veiculos = rv.veiculos;
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar.',
      )}</p>`;
      return;
    }
    const nomeVeiculo = (id: number): string => veiculos.find((v) => v.id === id)?.placa ?? `#${id}`;

    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>📊 Envios agendados <span class="sub-inline">${relatorios.length}</span></h3>
        <p class="dica">Cada relatório tem um link público: quem recebe abre a versão interativa
          (filtros, ordenação e modelos de impressão) sem precisar de login. Esta lista mostra só
          os envios recorrentes por e-mail.</p>
        ${
          relatorios.length === 0
            ? '<p class="dica">Nenhum envio agendado — o relatório comum é gerado na hora, ali embaixo, e não entra nesta lista.</p>'
            : `<div class="rolagem"><table class="tabela"><thead><tr>
                 <th>Nome</th><th>Veículos</th><th>Recorrência</th><th></th>
               </tr></thead><tbody>${relatorios
                 .map(
                   (r) => `<tr>
                     <td>${escapar(r.nome)}</td>
                     <td>${r.veiculo_ids.length === 0 ? 'todos' : r.veiculo_ids.map(nomeVeiculo).map(escapar).join(', ')}</td>
                     <td>${
                       { mensal: `mensal — todo dia ${r.dia_envio ?? 1}`, quinzenal: 'quinzenal — dias 1 e 16', semanal: 'semanal — toda segunda' }[r.periodicidade]
                     } → ${r.emails.map(escapar).join(', ')}${r.enviado_mes ? `<br><span class="sub-inline">último envio: ${escapar(r.enviado_mes)}</span>` : ''}</td>
                     <td class="fr-rel-acoes">
                       <a class="btn btn-claro" href="${escapar(r.url)}" target="_blank" rel="noopener">Abrir</a>
                       <button class="btn btn-claro" data-copiar="${escapar(r.url)}">🔗 Copiar link</button>
                       <button class="btn btn-claro" data-apagar="${r.id}">🗑️</button>
                     </td>
                   </tr>`,
                 )
                 .join('')}</tbody></table></div>`
        }
      </section>

      <section class="ped-painel">
        <h3>➕ Gerar relatório</h3>
        <div class="form-2col">
          <label class="rotulado"><span>Veículos <i class="sub-inline">nenhum marcado = todos</i></span>
            <span class="fr-rel-veiculos">${veiculos
              .map((v) => `<label><input type="checkbox" value="${v.id}"> ${escapar(v.placa)}</label>`)
              .join('')}</span></label>
          <label class="rotulado" id="rl-caixa-periodo"><span>Período — início e fim <i class="sub-inline">vazio = mês anterior</i></span>
            <span class="fr-rel-datas"><input id="rl-de" type="date"> <b>a</b> <input id="rl-ate" type="date"></span></label>
        </div>
        <label class="rotulado"><span>Nome do relatório <i class="sub-inline">gerado sozinho — pode mudar</i></span>
          <input id="rl-nome" maxlength="120"></label>

        <label class="rotulado fr-rel-recorrente-liga">
          <input type="checkbox" id="rl-recorrente"> <b>🔁 Envio recorrente por e-mail</b>
        </label>
        <div id="rl-caixa-recorrencia" hidden>
          <div class="form-2col">
            <label class="rotulado"><span>Com que frequência?</span>
              <select id="rl-periodicidade">
                <option value="mensal">o mês todo, do início ao fim</option>
                <option value="quinzenal">de 15 em 15 dias</option>
                <option value="semanal">semanal</option>
              </select></label>
            <label class="rotulado" id="rl-caixa-dia"><span>Dia do mês do envio</span>
              <input id="rl-dia" type="number" min="1" max="28" value="1" inputmode="numeric"></label>
          </div>
          <p class="dica" id="rl-quando-explica">Sai todo mês no dia marcado, cobrindo o mês anterior inteiro.</p>
          <label class="rotulado"><span>E-mails do envio <i class="sub-inline">separe por vírgula</i></span>
            <input id="rl-emails" placeholder="fulano@empresa.com, sicrano@empresa.com"></label>
        </div>

        <div class="conf-acoes"><button class="btn" id="rl-criar">📄 Gerar e abrir relatório</button></div>
      </section>`;

    raiz.querySelectorAll<HTMLElement>('[data-copiar]').forEach((b) => {
      b.addEventListener('click', () => {
        void navigator.clipboard
          .writeText(b.dataset['copiar']!)
          .then(() => avisar('Link copiado.'))
          .catch(() => avisarBloqueante(b.dataset['copiar']!));
      });
    });
    raiz.querySelectorAll<HTMLElement>('[data-apagar]').forEach((b) => {
      b.addEventListener('click', () => {
        if (!confirm('Apagar este relatório? O link dele para de funcionar.')) return;
        void api
          .del(`/api/frota/relatorios/${b.dataset['apagar']}`)
          .then(() => montarRelatorios())
          .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui apagar.'));
      });
    });
    // O NOME nasce sozinho do que está escolhido e para de se atualizar assim
    // que a pessoa mexer nele (21/08, Lucas).
    const nomeCampo = raiz.querySelector<HTMLInputElement>('#rl-nome')!;
    let nomeEditado = false;
    nomeCampo.addEventListener('input', () => { nomeEditado = true; });
    const ligaRecorrente = raiz.querySelector<HTMLInputElement>('#rl-recorrente')!;
    const selPeriodicidade = raiz.querySelector<HTMLSelectElement>('#rl-periodicidade')!;

    const dataCurta = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
    const gerarNome = (): void => {
      if (nomeEditado) return;
      const marcadas = [...raiz.querySelectorAll<HTMLInputElement>('.fr-rel-veiculos input:checked')];
      const quem =
        marcadas.length === 0
          ? 'toda a frota'
          : marcadas.map((c) => veiculos.find((v) => v.id === Number(c.value))?.placa ?? '').join(', ');
      if (ligaRecorrente.checked) {
        const ritmo = { mensal: 'mensal', quinzenal: 'quinzenal', semanal: 'semanal' }[
          selPeriodicidade.value as 'mensal' | 'quinzenal' | 'semanal'
        ];
        nomeCampo.value = `Abastecimentos — ${quem} — ${ritmo}`;
        return;
      }
      const de = raiz.querySelector<HTMLInputElement>('#rl-de')!.value;
      const ate = raiz.querySelector<HTMLInputElement>('#rl-ate')!.value;
      nomeCampo.value = `Abastecimentos — ${quem}${de && ate ? ` — ${dataCurta(de)} a ${dataCurta(ate)}` : ' — mês anterior'}`;
    };

    // Recorrente esconde o período (não existe início/fim em recorrente) e
    // mostra frequência + e-mails; o botão muda de verbo.
    const aoMudarModo = (): void => {
      const rec = ligaRecorrente.checked;
      raiz.querySelector<HTMLElement>('#rl-caixa-recorrencia')!.hidden = !rec;
      raiz.querySelector<HTMLElement>('#rl-caixa-periodo')!.hidden = rec;
      raiz.querySelector<HTMLElement>('#rl-criar')!.textContent = rec
        ? '✓ Agendar envio recorrente'
        : '📄 Gerar e abrir relatório';
      gerarNome();
    };
    ligaRecorrente.addEventListener('change', aoMudarModo);
    selPeriodicidade.addEventListener('change', () => {
      const v = selPeriodicidade.value;
      raiz.querySelector<HTMLElement>('#rl-caixa-dia')!.hidden = v !== 'mensal';
      raiz.querySelector<HTMLElement>('#rl-quando-explica')!.textContent =
        v === 'mensal'
          ? 'Sai todo mês no dia marcado, cobrindo o mês anterior inteiro.'
          : v === 'quinzenal'
            ? 'Sai nos dias 1 e 16, cobrindo a quinzena que acabou de fechar.'
            : 'Sai toda segunda-feira, cobrindo a semana anterior (segunda a domingo).';
      gerarNome();
    });
    raiz.querySelectorAll<HTMLInputElement>('.fr-rel-veiculos input').forEach((c) =>
      c.addEventListener('change', gerarNome),
    );
    raiz.querySelector('#rl-de')!.addEventListener('change', gerarNome);
    raiz.querySelector('#rl-ate')!.addEventListener('change', gerarNome);
    gerarNome();

    raiz.querySelector('#rl-criar')!.addEventListener('click', () => {
      const nome = nomeCampo.value.trim();
      if (nome.length < 2) return avisarBloqueante('Dê um nome ao relatório.');
      const corpo: Record<string, unknown> = {
        nome,
        veiculoIds: [...raiz.querySelectorAll<HTMLInputElement>('.fr-rel-veiculos input:checked')].map(
          (c) => Number(c.value),
        ),
      };
      if (ligaRecorrente.checked) {
        const emails = raiz
          .querySelector<HTMLInputElement>('#rl-emails')!
          .value.split(',')
          .map((x) => x.trim())
          .filter(Boolean);
        if (emails.length === 0) return avisarBloqueante('Envio recorrente precisa de pelo menos um e-mail.');
        corpo['recorrente'] = {
          periodicidade: selPeriodicidade.value,
          ...(selPeriodicidade.value === 'mensal'
            ? { diaEnvio: Number(raiz.querySelector<HTMLInputElement>('#rl-dia')!.value) || 1 }
            : {}),
          emails,
        };
      } else {
        const de = raiz.querySelector<HTMLInputElement>('#rl-de')!.value;
        const ate = raiz.querySelector<HTMLInputElement>('#rl-ate')!.value;
        if (de) corpo['periodoDe'] = de;
        if (ate) corpo['periodoAte'] = ate;
      }
      void api
        .post<{ url: string }>('/api/frota/relatorios', corpo)
        .then((r) => {
          if (ligaRecorrente.checked) {
            avisar('Envio recorrente agendado — está na lista.');
            return montarRelatorios();
          }
          // Relatório comum: abre na hora e NÃO entra na lista.
          window.open(r.url, '_blank', 'noopener');
          avisar('Relatório gerado — abriu numa aba nova.');
          return undefined;
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui criar.'));
    });
  }

  if (abaInicial !== 'veiculos') {
    casco
      .querySelectorAll('[data-fr-aba]')
      .forEach((x) => x.classList.toggle('chip-ativo', (x as HTMLElement).dataset['frAba'] === abaInicial));
    if (abaInicial === 'revalidacao') void montarRevalidacao();
    else void montarRelatorios();
  } else {
    void listar();
  }
}
